import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to="/admin" replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[#050507] px-4 py-10">
      <div className="orb orb-blue left-[-15%] top-[-10%] h-[520px] w-[520px] opacity-30 animate-slow-pulse" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "140px 140px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0c]/80 p-8 backdrop-blur-2xl md:p-10"
        data-testid="admin-login-card"
      >
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0055ff] font-display text-lg font-bold text-white">
            P
          </div>
          <div>
            <div className="font-display text-base font-semibold tracking-tight text-white">Parabolum</div>
            <div className="font-body text-[10px] tracking-[0.3em] uppercase text-white/40">Admin console</div>
          </div>
        </div>

        <h1 className="font-display text-3xl font-medium tracking-tight text-white">Sign in</h1>
        <p className="mt-2 font-body text-sm text-white/55">
          Restricted area. Editor access only.
        </p>

        <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
          <label className="block">
            <span className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/40">Email</span>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@parabolum.digital"
                data-testid="admin-login-email"
                autoComplete="email"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-3 pl-11 pr-4 font-body text-sm text-white placeholder-white/30 focus:border-[#0055ff]/50 focus:bg-white/[0.05] focus:outline-none"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/40">Password</span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                data-testid="admin-login-password"
                autoComplete="current-password"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-3 pl-11 pr-4 font-body text-sm text-white placeholder-white/30 focus:border-[#0055ff]/50 focus:bg-white/[0.05] focus:outline-none"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#0055ff] px-6 py-3.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff] disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
        </form>

        <div className="mt-7 border-t border-white/[0.06] pt-5 font-body text-[11px] text-white/35">
          ← <a href="/" className="hover:text-white">Back to website</a>
        </div>
      </motion.div>
    </div>
  );
}
